import { Injectable } from '@angular/core';

import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DeviceService } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { tap, withLatestFrom } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { betslipTypeStateSelector } from '../../types/selectors';
import { IBetslipTypeState } from '../../types/state';
import { ActionWithTracking, RewardTokensActions, TokenDialogClosedProps, TokenInfoOverlayHiddenProps } from '../actions';
import { AvailableTokensType, IRewardToken } from '../reward-tokens.model';
import { selectAllEligibleTokens, selectRewardTokens } from '../selectors';
import { getAllAssignedTokens } from '../services/linear-reward-tokens.utils';
import { getTokenTrackingType } from '../services/reward-tokens-tracking.utils';
import { getTokensType } from '../services/reward-tokens.utils';
import { TokensRecord } from '../state';

@Injectable()
export class RewardTokensTrackingEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private trackingService: TrackingService,
        private deviceService: DeviceService,
    ) {}

    trackSelectToken$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.selectToken, RewardTokensActions.selectSingleToken),
                withLatestFrom(this.store.select(selectRewardTokens)),
                tap(([action, tokensState]) => {
                    const selectToken = tokensState[action.payload.tokenId];

                    this.trackPageView(this.getTokenToggleTrackingAction(selectToken, true), action.payload.trackingSource);
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackToggleSelectToken$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.selectToken),
                withLatestFrom(this.store.select(selectRewardTokens)),
                tap(([action, tokensState]) => {
                    const selectToken = tokensState[action.payload.tokenId];
                    this.trackToggleTokenEvent(selectToken, 'added');
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackToggleDeselectToken$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.deselectToken),
                concatLatestFrom(() => [this.store.select(selectRewardTokens)]),
                tap(([action, tokensState]) => {
                    const selectToken = tokensState[action.payload.tokenId];
                    this.trackToggleTokenEvent(selectToken, 'remove');
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackAllDeselectToken$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.deselectAllToken),
                concatLatestFrom(() => [this.store.select(selectRewardTokens), this.store.select(betslipTypeStateSelector)]),
                tap(([action, tokens, types]: [ActionWithTracking, TokensRecord, IBetslipTypeState]) => {
                    const selectedTokens = getAllAssignedTokens(tokens, types);

                    for (const token of selectedTokens) {
                        this.trackPageView(this.getTokenToggleTrackingAction(token, false), action.trackingSource);
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackShowOverlayInfo$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.showOverlayInfo),
                withLatestFrom(this.store.select(selectAllEligibleTokens)),
                tap(([action, eligibleTokens]: [ActionWithTracking, IRewardToken[]]) => {
                    const availableTokensType = getTokensType(eligibleTokens);
                    const category = availableTokensType === AvailableTokensType.AccaBoost ? 'bets boost' : 'CRM Tokens';
                    const label = availableTokensType === AvailableTokensType.AccaBoost ? 'acca boost' : 'CRM Tokens';
                    this.trackEvent(
                        category,
                        label,
                        'click',
                        this.getTokenInfoType(availableTokensType),
                        availableTokensType === AvailableTokensType.AccaBoost ? 'more info' : undefined,
                        undefined,
                        'betslip',
                        action.trackingSource,
                    );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackBannerClick$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.bannerClick),
                tap((action) => {
                    this.trackEvent(
                        'Promo Hub',
                        'Entry Point',
                        'Click',
                        'Reward Banner',
                        this.deviceService.isMobile ? 'mobile' : 'desktop',
                        action.banner,
                        undefined,
                        action.trackingSource,
                    );
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackOpenDialogEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.openDialog),
                concatLatestFrom(() => [this.store.select(selectAllEligibleTokens)]),
                tap(([, eligibleTokens]: [ActionWithTracking, IRewardToken[]]) => {
                    const tokenTypes = eligibleTokens.map((t) => getTokensType([t]));
                    const tokensData = tokenTypes.map((token) => {
                        const tracking = getTokenDialogTrackData('load', token);

                        return {
                            [trackingConstants.COMPONENT_CATEGORY_EVENT]: tracking.category,
                            [trackingConstants.COMPONENT_LABEL_EVENT]: tracking.label,
                            [trackingConstants.COMPONENT_ACTION_EVENT]: tracking.action,
                            [trackingConstants.COMPONENT_EVENT_DETAILS]: tracking.details || NOT_APPLICABLE,
                            [trackingConstants.COMPONENT_POSITION_EVENT]: tracking.position || NOT_APPLICABLE,
                            [trackingConstants.COMPONENT_LOCATION_EVENT]: tracking.location || NOT_APPLICABLE,
                            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
                        };
                    });

                    this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, { [trackingConstants.BETS_BOOSTS]: tokensData });
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackCloseDialogEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.closeDialog),
                tap((action: TokenDialogClosedProps & Action) => {
                    if (action.closeButtonClicked) {
                        this.trackEvent('bets boost', 'bets boost drawer', 'exit', 'close x', 'bets boost drawer', NOT_APPLICABLE, 'betslip', {});
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackHideInfoOverlay$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.hideOverlayInfo),
                tap((action: TokenInfoOverlayHiddenProps & Action) => {
                    if (action.okButtonClicked) {
                        this.trackEvent('bets boost', 'rewards', 'click', 'got it', 'rewards overlay', NOT_APPLICABLE, 'betslip', {});
                    }
                    if (action.linkClicked) {
                        this.trackEvent('bets boost', 'rewards', 'click', 'my rewards page link', 'rewards overlay', NOT_APPLICABLE, 'betslip', {});
                    }
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    trackOverlayInfoTabClicked$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RewardTokensActions.overlayTabClicked),
                tap((action) => {
                    const tabType = action.payload.isAccaBoost ? 'acca boost' : 'rewards';
                    this.trackEvent('bets boost', 'rewards', 'click', tabType, 'rewards overlay', NOT_APPLICABLE, 'betslip', {});
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    private trackToggleTokenEvent(selectToken: IRewardToken, action: string) {
        const tokenType = getTokenTrackingType(selectToken.rewardTokenType, selectToken.customTitle);
        const tokenClickData = getTokenDialogTrackData('click', tokenType);
        if (selectToken.rewardTargetType === RewardTargetType.WelcomeOffer) {
            tokenClickData.position = tokenClickData.position + ' - welcome offer';
        }
        this.trackEvent(
            tokenClickData.category,
            tokenClickData.label,
            tokenClickData.action,
            tokenClickData.position,
            `${tokenClickData.details} - ${action}`,
            undefined,
            tokenClickData.location,
            undefined,
        );
    }

    private trackPageView(pageName: string, baseTracking: { [key: string]: string } | undefined): void {
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
            [trackingConstants.PAGE_NAME]: pageName,
            ...baseTracking,
        });
    }

    private trackEvent(
        category: string,
        label: string,
        action: string,
        position: string,
        details: string = NOT_APPLICABLE,
        url: string = NOT_APPLICABLE,
        location: string = NOT_APPLICABLE,
        baseTracking: { [key: string]: string } | undefined,
    ): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: category,
            [trackingConstants.COMPONENT_LABEL_EVENT]: label,
            [trackingConstants.COMPONENT_ACTION_EVENT]: action,
            [trackingConstants.COMPONENT_POSITION_EVENT]: position,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: location,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: details,
            [trackingConstants.COMPONENT_URL_CLICKED]: url,
            ...baseTracking,
        });
    }

    private getTokenToggleTrackingAction(token: IRewardToken, selected: boolean): string {
        return `M2_slip_action_${getTokenTrackingType(token.rewardTokenType, token.customTitle)}_toggled/${selected.toString()}`;
    }

    private getTokenInfoType(type: AvailableTokensType): string {
        switch (type) {
            case AvailableTokensType.FreeBet:
                return 'Freebet';
            case AvailableTokensType.OddsBoost:
                return 'Oddsboost';
            case AvailableTokensType.RiskFreeBet:
                return 'Riskfree';
            case AvailableTokensType.MultipleRewards:
                return 'Rewards';
            case AvailableTokensType.AccaBoost:
                return NOT_APPLICABLE;
            case AvailableTokensType.BetAndGet:
                return 'BetAndGet';
            default:
                return '';
        }
    }
}

const getTokenDialogTrackData = (action: string, position: string) => {
    const label = 'bets boost drawer';
    const details = 'eligible rewards';

    return { action, position, label, category: 'bets boost', location: 'betslip', details };
};
